import { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { API_BASE_URL } from "../../../constant/constant";

// Fallback FAQ data
const fallbackFAQs = [
  {
    id: "1",
    question: "What is NKENNEAi?",
    answer:
      "NKENNEAi is an AI-powered platform that provides advanced translation and speech recognition services specifically designed for African languages, helping bridge communication gaps and preserve linguistic diversity.",
  },
  {
    id: "2",
    question: "Which African languages are currently supported?",
    answer:
      "We currently support over 20 African languages including Swahili, Yoruba, Amharic, Hausa, Zulu, Xhosa, Igbo, Akan, and many more. We are continuously expanding our language support based on community needs.",
  },
  {
    id: "3",
    question: "How accurate is the translation and speech recognition?",
    answer:
      "Our AI models achieve 95%+ accuracy for supported languages. We continuously train and improve our models with native speaker input and real-world usage data to ensure the highest quality translations.",
  },
  {
    id: "4",
    question: "Who can use NKENNEAi's API?",
    answer:
      "Our API is available for developers, businesses, educational institutions, and organizations looking to integrate African language support into their applications. We offer flexible pricing plans for different use cases.",
  },
];

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null); // Single open item for accordion
  const location = useLocation().pathname;
  const [faqs, setFAQs] = useState(fallbackFAQs);
  const [loading, setLoading] = useState(true);

  // Get all FAQs from API
  const getFAQs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/public/faqs`);
      if (!response.ok) {
        throw new Error('Failed to fetch FAQs');
      }

      const data = await response.json();

      // Use fetched FAQs if available, otherwise use fallback
      if (data.faqs && Array.isArray(data.faqs) && data.faqs.length > 0) {
        setFAQs(data.faqs);
      } else {
        setFAQs(fallbackFAQs);
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      // Use fallback FAQs on error
      setFAQs(fallbackFAQs);
    } finally {
      setLoading(false);
    }
  };

  // Fetch FAQs on component mount
  useEffect(() => {
    getFAQs();
  }, []);

  const toggleItem = (index) => {
    setOpenIndex((prev) => (prev === index ? null : index)); // Toggle single item
  };

  return (
    <section className="py-20 px-6">
      <div className="container mx-auto max-w-4xl">
        <div className="text-center mb-16">
          <div className="text-[#ef8702] text-center font-black text-lg tracking-[0.8px] mb-4">
            FAQ
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#10281E] mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-[#10281E] max-w-2xl mx-auto">
            Browse through our frequently asked questions and you might just
            find the answer to your question.
          </p>
        </div>

        <div className="space-y-4 mb-12">
          {loading ? (
            <div className="text-center py-8">
              <p className="text-[#10281E]">Loading FAQs...</p>
            </div>
          ) : faqs.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-[#10281E]">No FAQs available at the moment.</p>
            </div>
          ) : (
            faqs.map((item, index) => (
              <div
                key={item.id || index}
                className="bg-card border border-border rounded-lg shadow-card overflow-hidden"
              >
                <button
                  onClick={() => toggleItem(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-muted/50 transition-colors"
                >
                  <span className="font-semibold text-[#10281E]">
                    {item.question}
                  </span>
                  <ChevronDown
                    className={`h-5 w-5 text-muted-foreground transition-transform duration-200 ${openIndex === index ? "rotate-180" : ""
                      }`}
                  />
                </button>
                <div
                  className={`px-6 overflow-hidden transition-all duration-200 ease-in-out ${openIndex === index
                    ? "pb-4 max-h-96 opacity-100"
                    : "pb-0 max-h-0 opacity-0"
                    }`}
                >
                  <p className="text-[#10281E] leading-relaxed">{item.answer}</p>
                </div>
              </div>
            ))
          )}
        </div>
        {location === "/faqs" ? (
          ""
        ) : (
          <div className="text-center">
            <Link to="/faqs">
              <button className="bg-[#1E6C56] text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors">
                See More
              </button>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default FAQSection;
